// src/components/ViewportProbeModal.tsx
import * as React from "react";
import { createPortal } from "react-dom";

type Props = { open: boolean; onClose: () => void };

function readAndroid() {
    const A: any = (window as any).Android;
    return {
        has: !!(A && typeof A.getWebViewSize === "function"),
        viewSize: A?.getWebViewSize?.() ?? null,
        wPx: A?.getViewWidthPx?.() ?? null,
        hPx: A?.getViewHeightPx?.() ?? null,
    };
}

export function ViewportProbeModal({ open, onClose }: Props) {
    const [boxMaxH, setBoxMaxH] = React.useState<number>(
        (window.visualViewport?.height || window.innerHeight || 640) - 32
    );
    const [probe, setProbe] = React.useState<any>({});

    const measure = React.useCallback(() => {
        const vv = (window as any).visualViewport;
        const vw = Math.round(vv?.width ?? window.innerWidth);
        const vh = Math.round(vv?.height ?? window.innerHeight);
        const scale = vv?.scale ?? 1;

        const docW = document.documentElement.clientWidth;
        const docH = document.documentElement.clientHeight;

        const scrW = (window.screen?.width ?? 0);
        const scrH = (window.screen?.height ?? 0);

        const android = readAndroid();

        setProbe({
            visualViewport: { width: vw, height: vh, scale },
            window: { innerWidth: window.innerWidth, innerHeight: window.innerHeight, dpr: window.devicePixelRatio },
            documentElement: { clientWidth: docW, clientHeight: docH },
            screen: { width: scrW, height: scrH },
            android,
            ts: new Date().toISOString(),
        });

        setBoxMaxH(Math.min((vv?.height || window.innerHeight) - 32, 680));
    }, []);

    React.useEffect(() => {
        if (!open) return;
        // 스크롤 잠금
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        measure();
        const onResize = () => measure();
        window.addEventListener("resize", onResize);
        (window as any).visualViewport?.addEventListener?.("resize", onResize);

        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("resize", onResize);
            (window as any).visualViewport?.removeEventListener?.("resize", onResize);
        };
    }, [open, measure]);

    if (!open) return null;

    const node = (
        <div
            role="dialog"
            aria-modal="true"
            style={{
                position: "fixed",
                inset: 0,                       // 전체 화면 덮음
                zIndex: 3000,
                background: "rgba(0,0,0,.45)",
                display: "grid",
                placeItems: "center",
                padding: "16px",
                // iOS/안드로이드 하단 제스처 영역 여유
                paddingBottom: "max(16px, env(safe-area-inset-bottom))",
            }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: "min(680px, 96vw)",
                    maxHeight: `${Math.max(320, boxMaxH)}px`, // 동적 vh 보정
                    overflow: "auto",
                    background: "#fff",
                    borderRadius: 12,
                    boxShadow: "0 12px 30px rgba(0,0,0,.2)",
                    padding: 16,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                    <b style={{ fontSize: 18 }}>Viewport Probe</b>
                    <div style={{ display: "flex", gap: 8 }}>
                        <button
                            onClick={measure}
                            style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #000", background: "#fff", cursor: "pointer", fontWeight: 800 }}
                        >Refresh</button>
                        <button
                            onClick={onClose}
                            style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #000", background: "#000", color: "#fff", cursor: "pointer", fontWeight: 800 }}
                        >닫기</button>
                    </div>
                </div>

                <pre style={{ margin: 0, fontSize: 12, lineHeight: "18px", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {JSON.stringify(probe, null, 2)}
        </pre>
            </div>
        </div>
    );

    return createPortal(node, document.body);
}
